import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const MAX_MB = 5;
const DOC_MAX_MB = {
  handover_sheet: 5,
  termination_letter: 5,
  letter_of_undertaking: 20,
};
const fileToB64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Read error"));
    r.onload = () => resolve(String(r.result || "").split(",")[1] || "");
    r.readAsDataURL(file);
  });

const CLEAR = ["yes", "no", "pending"];
const TERM = ["yes", "no", "na"];

const empty = {
  exit_discussion: "",
  exit_reason: "",
  hr_clearance: "",
  it_clearance: "",
  finance_clearance: "",
  admin_clearance: "",
  reporting_manager_clearance: "",
  handover_status: "",
  exit_date: "",
  termination: "",
  termination_reason: "",
};

export default function EmployeeExit() {
  const { employeeDbId } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const r = await api.get(`/employees/${employeeDbId}/exit`);
    const d = r.data;
    setMeta({
      employee_id: d.employee_id,
      employee_name: d.employee_name,
      employment_status: d.employment_status,
      initiated_at: d.initiated_at,
      initiated_by: d.initiated_by,
      updated_at: d.updated_at,
      updated_by: d.updated_by,
      documents: d.documents || {},
    });
    setForm({
      exit_discussion: d.exit_discussion || "",
      exit_reason: d.exit_reason || "",
      hr_clearance: d.hr_clearance || "",
      it_clearance: d.it_clearance || "",
      finance_clearance: d.finance_clearance || "",
      admin_clearance: d.admin_clearance || "",
      reporting_manager_clearance: d.reporting_manager_clearance || "",
      handover_status: d.handover_status || "",
      exit_date: d.exit_date || "",
      termination: d.termination || "",
      termination_reason: d.termination_reason || "",
    });
  };

  useEffect(() => {
    (async () => {
      try { await load(); }
      catch (e) { setErr(e?.response?.data?.detail || "Failed to load exit info"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeDbId]);

  if (err) return <div className="p-8 text-error">{err}</div>;
  if (!meta) return <div className="p-8 text-quiet">Loading…</div>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async (setStatus) => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      if (setStatus) payload.set_status = setStatus;
      await api.put(`/employees/${employeeDbId}/exit`, payload);
      toast.success(setStatus ? `Saved · status set to ${setStatus}` : "Saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save exit");
    } finally { setBusy(false); }
  };

  const uploadDoc = async (doc, file) => {
    const cap = DOC_MAX_MB[doc] ?? MAX_MB;
    if (file.size > cap * 1024 * 1024) return toast.error(`${file.name} exceeds ${cap} MB`);
    try {
      const b64 = await fileToB64(file);
      await api.put(`/employees/${employeeDbId}/exit/documents/${doc}`, { filename: file.name, b64 });
      toast.success(`${doc.replace(/_/g, " ")} uploaded`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to upload"); }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/employees/${employeeDbId}/exit/documents/${doc}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/employees/${employeeDbId}/exit/documents/${doc}`);
      const { filename, b64 } = r.data;
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to download"); }
  };

  const isExited = meta.employment_status === "exited";

  return (
    <div className="p-8 max-w-[1100px] mx-auto" data-testid="employee-exit-page">
      <Link to={`/employees/${employeeDbId}`} className="text-xs text-quiet hover:text-ink font-mono">← Employee</Link>
      <div className="mt-4 border-b border-rule pb-6 flex items-start justify-between gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            {meta.employee_id} · Status: <span className="text-ink">{meta.employment_status}</span>
          </div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Exit · {meta.employee_name}</h1>
          <p className="text-sm text-quiet mt-3">
            {meta.initiated_at
              ? `Initiated ${new Date(meta.initiated_at).toLocaleString()} by ${meta.initiated_by}`
              : "Not yet initiated. Fill and save to open the exit process."}
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end shrink-0">
          {meta.employment_status === "active" && (
            <button
              data-testid="start-exit-btn"
              disabled={busy}
              onClick={() => save("inactive")}
              className="bg-klein hover:bg-klein-hover text-white px-4 py-2 text-xs font-mono uppercase tracking-wider disabled:opacity-50"
            >Start exit · mark inactive</button>
          )}
          {!isExited && (
            <button
              data-testid="finalize-exit-btn"
              disabled={busy}
              onClick={() => {
                if (window.confirm("Mark employee as EXITED? This is a one-way action for reporting purposes.")) save("exited");
              }}
              className="border border-error text-error hover:bg-error hover:text-white px-4 py-2 text-xs font-mono uppercase tracking-wider disabled:opacity-50"
            >Finalize exit · mark exited</button>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <Section title="Exit discussion">
          <Grid>
            <Field label="Discussion">
              <Select testId="ex-discussion" value={form.exit_discussion} onChange={set("exit_discussion")}
                options={[["","—"],["done","Done"],["pending","Pending"]]} />
            </Field>
            <Field label="Exit reason" full>
              <Textarea testId="ex-reason" value={form.exit_reason} onChange={set("exit_reason")} />
            </Field>
          </Grid>
        </Section>

        <Section title="Clearances" hint="Yes · No · Pending">
          <Grid cols={5}>
            <ClearanceField label="HR" testId="ex-hr" value={form.hr_clearance} onChange={set("hr_clearance")} />
            <ClearanceField label="IT" testId="ex-it" value={form.it_clearance} onChange={set("it_clearance")} />
            <ClearanceField label="Finance" testId="ex-finance" value={form.finance_clearance} onChange={set("finance_clearance")} />
            <ClearanceField label="Admin" testId="ex-admin" value={form.admin_clearance} onChange={set("admin_clearance")} />
            <ClearanceField label="Reporting Manager" testId="ex-rm" value={form.reporting_manager_clearance} onChange={set("reporting_manager_clearance")} />
          </Grid>
        </Section>

        <Section title="Handover">
          <Grid>
            <Field label="Handover status">
              <Select testId="ex-handover-status" value={form.handover_status} onChange={set("handover_status")}
                options={[["","—"],...CLEAR.map(v => [v, v[0].toUpperCase()+v.slice(1)])]} />
            </Field>
            <Field label="Handover sheet" full>
              <DocSlot testId="ex-doc-handover" doc={meta.documents?.handover_sheet}
                onPick={(f) => uploadDoc("handover_sheet", f)}
                onDownload={() => downloadDoc("handover_sheet")}
                onRemove={() => removeDoc("handover_sheet")} />
            </Field>
          </Grid>
        </Section>

        <Section title="Letter of undertaking" hint={`Max 20 MB`}>
          <Grid>
            <Field label="Letter of undertaking" full>
              <DocSlot testId="ex-doc-lou" doc={meta.documents?.letter_of_undertaking}
                onPick={(f) => uploadDoc("letter_of_undertaking", f)}
                onDownload={() => downloadDoc("letter_of_undertaking")}
                onRemove={() => removeDoc("letter_of_undertaking")} />
            </Field>
          </Grid>
        </Section>

        <Section title="Exit date">
          <Grid>
            <Field label="Exit date">
              <Input testId="ex-date" type="date" value={form.exit_date} onChange={set("exit_date")} />
            </Field>
          </Grid>
        </Section>

        <Section title="Termination">
          <Grid>
            <Field label="Termination">
              <Select testId="ex-term" value={form.termination} onChange={set("termination")}
                options={[["","—"],["yes","Yes"],["no","No"],["na","NA"]]} />
            </Field>
            <Field label="Termination reason" full>
              <Textarea testId="ex-term-reason" value={form.termination_reason} onChange={set("termination_reason")} />
            </Field>
            <Field label="Termination letter" full>
              <DocSlot testId="ex-doc-term" doc={meta.documents?.termination_letter}
                onPick={(f) => uploadDoc("termination_letter", f)}
                onDownload={() => downloadDoc("termination_letter")}
                onRemove={() => removeDoc("termination_letter")} />
            </Field>
          </Grid>
        </Section>

        <div className="sticky bottom-0 bg-white border-t border-rule -mx-8 px-8 py-4 flex justify-between items-center">
          <div className="text-xs text-quiet font-mono">
            {meta.updated_at ? `Saved ${new Date(meta.updated_at).toLocaleString()} · ${meta.updated_by}` : "Not saved"}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => nav(`/employees/${employeeDbId}`)}
              className="border border-ink px-4 py-2 text-sm hover:bg-paper"
              data-testid="exit-cancel-btn"
            >Back</button>
            <button
              disabled={busy}
              onClick={() => save(null)}
              data-testid="exit-save-btn"
              className="bg-klein hover:bg-klein-hover text-white px-6 py-2 text-sm font-medium disabled:opacity-50"
            >{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, hint, children }) => (
  <section className="bg-white border border-rule">
    <div className="px-5 py-4 border-b border-rule flex items-baseline justify-between">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{title}</div>
      {hint && <div className="font-mono text-[10px] text-quiet">{hint}</div>}
    </div>
    <div className="p-5">{children}</div>
  </section>
);
const Grid = ({ cols = 3, children }) => {
  const cls = cols === 5 ? "md:grid-cols-5" : "md:grid-cols-3";
  return <div className={`grid grid-cols-1 ${cls} gap-x-6 gap-y-4`}>{children}</div>;
};
const Field = ({ label, full, children }) => (
  <div className={full ? "md:col-span-3" : ""}>
    <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-1">{label}</div>
    {children}
  </div>
);
const Input = ({ testId, ...p }) => (
  <input data-testid={testId} {...p} className="w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white" />
);
const Textarea = ({ testId, ...p }) => (
  <textarea data-testid={testId} rows={3} {...p} className="w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white" />
);
const Select = ({ testId, options, ...p }) => (
  <select data-testid={testId} {...p} className="w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const ClearanceField = ({ label, value, onChange, testId }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-1">{label}</div>
    <div className="flex gap-1">
      {[["", "—"], ["yes", "Yes"], ["no", "No"], ["pending", "…"]].map(([v, l]) => (
        <button
          key={v || "empty"}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`${testId}-${v || "empty"}`}
          className={`flex-1 px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider border ${
            value === v
              ? v === "yes" ? "bg-[#DCFCE7] border-[#166534] text-[#166534]"
              : v === "no" ? "bg-[#FEE2E2] border-[#991B1B] text-[#991B1B]"
              : v === "pending" ? "bg-[#FEF3C7] border-[#92400E] text-[#92400E]"
              : "bg-paper border-rule text-quiet"
              : "border-rule text-quiet hover:border-ink"
          }`}
        >{l}</button>
      ))}
    </div>
  </div>
);

const DocSlot = ({ testId, doc, onPick, onDownload, onRemove }) => {
  const ref = useRef(null);
  const pick = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    await onPick(f);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="border border-rule p-3 bg-paper flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs text-quiet font-mono uppercase tracking-wider">{doc?.filename ? "Uploaded" : "Not uploaded"}</div>
        <div className="text-sm text-ink truncate">{doc?.filename || "—"}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {doc?.filename && (
          <>
            <button data-testid={`${testId}-download`} onClick={onDownload}
              className="text-xs font-mono uppercase tracking-wider text-klein hover:underline">Download ↓</button>
            <button data-testid={`${testId}-remove`} onClick={onRemove}
              className="text-xs font-mono uppercase tracking-wider text-error hover:underline">Remove</button>
          </>
        )}
        <label data-testid={`${testId}-upload-label`}
          className="text-xs font-mono uppercase tracking-wider text-white bg-klein hover:bg-klein-hover px-2 py-1 cursor-pointer">
          {doc?.filename ? "Replace" : "Upload"}
          <input ref={ref} data-testid={`${testId}-upload`} type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={pick} className="hidden" />
        </label>
      </div>
    </div>
  );
};
